import React from 'react';
import { Card, Descriptions, Divider, Typography, Row, Col, Button } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import { PayrollEntry, PayrollPeriod } from '../types';

const { Title, Text } = Typography;

interface PayslipCardProps {
  entry: PayrollEntry;
  period?: PayrollPeriod;
  currency?: string;
}

const fmt = (n: number) => new Intl.NumberFormat('en-TZ', { minimumFractionDigits: 2 }).format(n);

const PayslipCard: React.FC<PayslipCardProps> = ({ entry, period, currency = 'TZS' }) => {
  const periodName = period?.periodName ?? entry.payrollPeriodName ?? '—';
  const periodRange = period ? `${period.startDate} – ${period.endDate}` : '—';
  const handlePrint = () => window.print();

  return (
    <Card
      className="payslip-card"
      title={
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={5} style={{ margin: 0 }}>Pay Slip — {periodName}</Title>
          </Col>
          <Col>
            <Button icon={<PrinterOutlined />} onClick={handlePrint} size="small">Print</Button>
          </Col>
        </Row>
      }
    >
      <Descriptions bordered column={2} size="small">
        <Descriptions.Item label="Employee">{entry.employeeName}</Descriptions.Item>
        <Descriptions.Item label="Employee No.">{entry.employeeNumber}</Descriptions.Item>
        <Descriptions.Item label="Department">{entry.departmentName ?? '—'}</Descriptions.Item>
        <Descriptions.Item label="Period">{periodRange}</Descriptions.Item>
        <Descriptions.Item label="Days Worked">{entry.daysWorked}</Descriptions.Item>
        <Descriptions.Item label="Days Absent">{entry.daysAbsent}</Descriptions.Item>
      </Descriptions>

      <Divider>Earnings</Divider>
      <Descriptions column={2} size="small">
        <Descriptions.Item label="Basic Salary">{currency} {fmt(entry.basicSalary)}</Descriptions.Item>
        <Descriptions.Item label="Housing Allowance">{currency} {fmt(entry.housingAllowance)}</Descriptions.Item>
        <Descriptions.Item label="Transport Allowance">{currency} {fmt(entry.transportAllowance)}</Descriptions.Item>
        <Descriptions.Item label="Medical Allowance">{currency} {fmt(entry.medicalAllowance)}</Descriptions.Item>
        <Descriptions.Item label="Other Allowances">{currency} {fmt(entry.otherAllowances)}</Descriptions.Item>
        <Descriptions.Item label="Overtime">{currency} {fmt(entry.overtimeAmount)}</Descriptions.Item>
        <Descriptions.Item label="Bonus">{currency} {fmt(entry.bonus)}</Descriptions.Item>
        <Descriptions.Item label="Commission">{currency} {fmt(entry.commission)}</Descriptions.Item>
      </Descriptions>
      <div style={{ textAlign: 'right', marginTop: 8 }}>
        <Text strong>Gross Pay: {currency} {fmt(entry.grossPay)}</Text>
      </div>

      <Divider>Deductions</Divider>
      <Descriptions column={2} size="small">
        <Descriptions.Item label="PAYE Tax">{currency} {fmt(entry.taxDeduction)}</Descriptions.Item>
        <Descriptions.Item label="NSSF">{currency} {fmt(entry.socialSecurityDeduction)}</Descriptions.Item>
        <Descriptions.Item label="Pension">{currency} {fmt(entry.pensionDeduction)}</Descriptions.Item>
        <Descriptions.Item label="Loan Deduction">{currency} {fmt(entry.loanDeduction)}</Descriptions.Item>
        <Descriptions.Item label="Other Deductions">{currency} {fmt(entry.otherDeductions)}</Descriptions.Item>
      </Descriptions>
      <div style={{ textAlign: 'right', marginTop: 8 }}>
        <Text strong type="danger">Total Deductions: {currency} {fmt(entry.totalDeductions)}</Text>
      </div>

      <Divider />
      <div style={{ textAlign: 'right' }}>
        <Title level={4} style={{ margin: 0 }}>
          Net Pay: <Text type="success">{currency} {fmt(entry.netPay)}</Text>
        </Title>
      </div>
    </Card>
  );
};

export default PayslipCard;
